import React, { useContext } from "react";
import { SettingsContext } from "./SettingsContext.tsx";
import { RadioButton } from "primereact/radiobutton";
import { Checkbox } from "primereact/checkbox";
import { SelectButton } from "primereact/selectbutton";
import { Coins, MoneyQuestionOptions, MoneyRangeOptions } from "../Constants.ts";
import { withLabels } from "../Labels.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

/**
 * What a money sheet asks, how large the amounts get, and — for counting coins —
 * which coins may appear.
 *
 * Under a dollar keeps every problem inside the cents column, which is where
 * money practice starts; the larger limits bring in the dollars column and,
 * with it, borrowing across the decimal point.
 */
export function MoneySettings() {
    const { settings, setSettings } = useContext(SettingsContext);
    const t = useTranslation();

    function handleRangeChange(event) {
        setSettings({ ...settings, MoneyMaxDollars: Number(event.target.value) });
    }

    function handleQuestionChange(value: MoneyQuestionOptions) {
        if (!value) return;
        setSettings({ ...settings, MoneyQuestion: value });
    }

    function toggleCoin(name: string) {
        const current = settings.MoneyCoins ?? [];
        const next = current.includes(name)
            ? current.filter((c) => c !== name)
            : [...current, name];
        setSettings({ ...settings, MoneyCoins: next });
    }

    const showCoins =
        settings.MoneyQuestion === MoneyQuestionOptions.CountingCoins ||
        settings.MoneyQuestion === MoneyQuestionOptions.Mixed;

    return (
        <div className="moneySettingsContainer">
            <div className="regroupingContainer">
                <SelectButton
                    value={settings.MoneyQuestion}
                    onChange={(e) => handleQuestionChange(e.value)}
                    options={withLabels(Object.values(MoneyQuestionOptions), settings.Language)}
                    className="select regrouping"
                    aria-label={t('settings.moneyQuestionTypeAria')}
                />
            </div>

            {/* The dollar range means nothing to a pure counting-coins sheet,
                but Mixed and Making Change use it, so it stays shown. */}
            <div className="selectContainer">
                <span className="settingLabel">{t('settings.amountsLabel')}</span>
            </div>
            <div className="radioButtons" role="radiogroup" aria-label={t('settings.moneyAmountsAria')}>
                {MoneyRangeOptions.map((option) => (
                    <div key={option.maxDollars}>
                        <RadioButton
                            inputId={`moneyRange-${option.maxDollars}`}
                            value={option.maxDollars}
                            checked={settings.MoneyMaxDollars === option.maxDollars}
                            onChange={handleRangeChange}
                        />
                        <label htmlFor={`moneyRange-${option.maxDollars}`}>{option.label}</label>
                    </div>
                ))}
            </div>

            {showCoins && (
                <>
                    <div className="selectContainer">
                        <span className="settingLabel">{t('settings.coinsLabel')}</span>
                    </div>
                    <div className="radioButtons" role="group" aria-label={t('settings.coinsAria')}>
                        {Coins.map((coin) => (
                            <div key={coin.name}>
                                <Checkbox
                                    inputId={`coin-${coin.name}`}
                                    checked={(settings.MoneyCoins ?? []).includes(coin.name)}
                                    onChange={() => toggleCoin(coin.name)}
                                />
                                <label htmlFor={`coin-${coin.name}`}>{coin.name}</label>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

export default MoneySettings;
